import React, { useEffect, useState, useMemo, useCallback, useRef, memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { updateWorkflowSessionState } from '../../store/reducers';
import { createValidationErrorsHash, createEditableDataHash } from '../../utils/hashUtils';
import { scroller } from "react-scroll";
import { getMainTranslation } from '../../hooks/useLanguage';
import { ChevronDown, ChevronRight } from 'lucide-react';
import '../Sidebar/Sidebar.css';
import { getFieldId } from '../../utils/fieldIdUtils';
import { getFieldDisplayName, extractBaseFieldName } from '../../utils/fieldDisplayUtils';
import {
  getAllRequiredFieldIds, 
  organizeFieldsFromData, 
  addMissingFieldsFromErrors,
  hasInputElement,
  clearInputElementCache
} from '../../utils/fieldOverviewUtils';
import { 
  hasValidationErrors, 
  hasTaricJsonErrors, 
  getFieldLabelFromValidationErrors 
} from '../../utils/errorCheckUtils';

import { AiIconSvg, FieldOverviewIconSvg } from './FieldOverviewIcons';
import AiSuggestionsContainer from './AiSuggestionsContainer';

// Field Card Component
const FieldCard = React.memo(({ field, index, validationErrorFieldIds, validationErrors, activeSubTab, requiredFilter, isSpanish, onFieldClick, dataHash }) => {
  const fieldId = field.fieldId || getFieldId(field.field, field.declarationIndex, field.itemIndex, field.docIndex, field.embajaIndex, field.marcaIndex);
  const [hasInput, setHasInput] = useState(() => hasInputElement(fieldId));
  
  useEffect(() => {
    if (dataHash) clearInputElementCache();
    
    const check = () => setHasInput(hasInputElement(fieldId));
    
    check(); // Immediate
    const timeouts = [
      setTimeout(check, 100),
      setTimeout(check, 500),
      setTimeout(check, 1000)
    ];
    
    return () => timeouts.forEach(clearTimeout);
  }, [fieldId, dataHash]);
  
  if (!hasInput) return null;
  
  const isRequired = validationErrorFieldIds?.has(fieldId) || false;
  const fieldLabel = getFieldLabelFromValidationErrors(fieldId, validationErrors) || getFieldDisplayName(field.field, isSpanish);
  const bgColor = !field.hasValue ? 'bg-red-50' : 'bg-secondary';
  const showValue = activeSubTab !== 'missingFields' || field.hasValue;
  const valueText = field.hasValue 
    ? (typeof field.value === 'object' ? JSON.stringify(field.value) : String(field.value))
    : (isSpanish ? 'Sin valor' : 'No value');
  
  return (
    <div 
      className={`p-3 px-3 rounded-lg text-sm break-words ${bgColor} ${hasInput ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''} relative`}
      onClick={hasInput ? (e) => onFieldClick(e, fieldId, field.hasValue) : undefined}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0 flex gap-1 truncate">
          <span className={`font-semibold text-xs break-words ${hasInput ? 'text-gray-700' : 'text-gray-500'}`}>
            {fieldLabel}
          </span>
          <div className="text-[8px] text-gray-500 font-mono break-all truncate bottom-[-0px] relative">
            {extractBaseFieldName(field.field)}
          </div>
        </div>
        {isRequired && requiredFilter === 'all' && (
          <span className="text-[10px] text-red-600 font-semibold whitespace-nowrap flex-shrink-0">
            {isSpanish ? 'Requerido' : 'Required'}
          </span>
        )}
      </div>
      {showValue && (
        <div className={`truncate text-xs break-words ${field.hasValue ? 'text-gray-600' : 'text-red-500'}`}>
          {valueText}
        </div>
      )}
    </div>
  );
});

FieldCard.displayName = 'FieldCard';

// Helper to render field groups
const renderFieldGroup = (fields, prefix, FieldCard, props) => {
  if (!fields.length) return null;
  return (
    <div className="space-y-2 mb-2 ml-2">
      {fields.map((field, index) => (
        <FieldCard key={`${prefix}-${field.field}-${index}-${props.dataHash || ''}`} field={field} index={index} {...props} />
      ))}
    </div>
  );
};

// Field List Content Component - respects Declaration Summary collapse (declarationSummaryCollapsedByIndex)
const FieldListContent = React.memo(({ organizedFields, activeSubTab, requiredFilter, validationErrorFieldIds, validationErrors, isSpanish, dataHash, declarationSummaryCollapsedByIndex = {}, onExpandExport }) => {
  const handleFieldClick = useCallback((e, fieldId, hasValue) => {
    e.preventDefault();
    const element = document.getElementById(fieldId);
    if (element) {
      scroller.scrollTo(fieldId, { duration: 100, offset: -300, smooth: "easeInOutQuint", containerId: "analysis-report" });
      setTimeout(() => {
        element.classList.add(hasValue ? "highlight-non-missing" : "highlight-error");
        setTimeout(() => element.classList.remove(hasValue ? "highlight-non-missing" : "highlight-error"), 5000);
      }, 300);
    }
  }, []);
  
  const filterField = useCallback((field) => {
    // Use cached fieldId if available (optimization #2), otherwise compute it once
    const fieldId = field.fieldId || getFieldId(field.field, field.declarationIndex, field.itemIndex, field.docIndex, field.embajaIndex, field.marcaIndex);
    
    // Check required filter first (early return for better performance)
    if (requiredFilter !== 'all') {
      const isRequired = validationErrorFieldIds?.has(fieldId) || false;
      if ((requiredFilter === 'required' && !isRequired) || (requiredFilter === 'nonRequired' && isRequired)) return false;
                }
                
    // Apply subtab filters
    if (activeSubTab === 'allFields') {
      const fieldName = extractBaseFieldName(field.field);
      return fieldName !== 'version' && field.field !== 'version';
                }
    
    if (activeSubTab === 'missingFields') return !field.hasValue;
    if (activeSubTab === 'nonMissingFields') return field.hasValue;
    
                return true;
  }, [activeSubTab, requiredFilter, validationErrorFieldIds]);
  
  const filteredFields = useMemo(() => {
    return organizedFields.map(decl => ({
      ...decl,
      declarationFields: decl.declarationFields.filter(filterField),
      itemFields: decl.itemFields.map(item => ({
        ...item,
        fields: item.fields.filter(filterField),
        documentFields: item.documentFields.map(doc => ({ ...doc, fields: doc.fields.filter(filterField) })).filter(doc => doc.fields.length > 0),
        packageFields: item.packageFields.map(pkg => ({ ...pkg, fields: pkg.fields.filter(filterField) })).filter(pkg => pkg.fields.length > 0),
      }))
    }));
  }, [organizedFields, filterField]);
              
  const hasFields = useMemo(() => {
    return filteredFields.some(decl => 
      decl.declarationFields.length > 0 || 
      decl.itemFields.some(item => item.fields.length > 0 || item.documentFields.length > 0 || item.packageFields.length > 0)
      );
  }, [filteredFields]);
              
              if (!hasFields) {
    const messages = {
      missingFields: isSpanish ? 'No hay campos faltantes' : 'No missing fields',
      nonMissingFields: isSpanish ? 'No hay campos completados' : 'No non-missing fields',
      allFields: isSpanish ? 'No hay campos disponibles' : 'No fields available',
    };
    return <p className="ml-4 text-gray-500 text-sm">{messages[activeSubTab] || messages.allFields}</p>;
                  }
                
  const cardProps = { validationErrorFieldIds, validationErrors, activeSubTab, requiredFilter, isSpanish, onFieldClick: handleFieldClick, dataHash };
                
                return (
      <div className="space-y-4">
      {filteredFields.map((decl, declIndex) => {
        const hasAnyFields = decl.declarationFields.length > 0 || 
          decl.itemFields.some(item => item.fields.length > 0 || item.documentFields.length > 0 || item.packageFields.length > 0);
                    if (!hasAnyFields) return null;
                    const isExportCollapsed = declarationSummaryCollapsedByIndex[decl.declarationIndex] === true;
                    return (
                      <div key={declIndex} className="mb-4">
                        <h3
                          className={`font-semibold text-base mb-2 text-gray-700 ml-5 flex items-center gap-1 ${isExportCollapsed ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
                          onClick={isExportCollapsed && onExpandExport ? () => onExpandExport(decl.declarationIndex) : undefined}
                          onKeyDown={isExportCollapsed && onExpandExport ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onExpandExport(decl.declarationIndex); } } : undefined}
                          role={isExportCollapsed && onExpandExport ? 'button' : undefined}
                          tabIndex={isExportCollapsed && onExpandExport ? 0 : undefined}
                        >
              {isSpanish ? "Expediente" : "Export"} #{decl.declarationIndex + 1}
              {isExportCollapsed && <ChevronRight className="size-4 text-gray-500 flex-shrink-0 mt-1" />}  
                        </h3>
                        
            {!isExportCollapsed && renderFieldGroup(decl.declarationFields, `decl`, FieldCard, cardProps)}
            
            {!isExportCollapsed && decl.itemFields.filter(item => item != null).map((item, itemIdx) => {
              const hasItemData = item.fields.length > 0 || item.documentFields.length > 0 || item.packageFields.length > 0;
                          if (!hasItemData) return null;
                          
                          return (
                            <div key={itemIdx} className="ml-4 mb-3">
                              <h4 className="font-medium text-sm mb-2 text-gray-600 ml-5">
                    {isSpanish ? "Partida" : "Item"} #{item.itemIndex + 1}
                              </h4>
                              
                  {renderFieldGroup(item.fields, `item`, FieldCard, cardProps)}
                  
                  {item.documentFields.length > 0 && (
                                <div className="ml-4 mb-2">
                                  <h5 className="font-medium text-sm mb-1 text-gray-500 ml-9">
                        {isSpanish ? "Documentos" : "Documents"}
                                  </h5>
                      {item.documentFields.map((doc, docIdx) => (
                                    <div key={docIdx} className="ml-4 mb-2">
                                      <p className="text-xs font-medium mb-1 text-gray-500 ml-5">
                            {isSpanish ? "Documento" : "Document"} #{doc.docIndex + 1}
                                      </p>
                          {renderFieldGroup(doc.fields, `doc`, FieldCard, cardProps)}
                                    </div>
                                  ))}
                                </div>
                              )}
                              
                  {item.packageFields.length > 0 && (
                                <div className="ml-4 mb-2">
                                  <h5 className="font-medium text-sm mb-1 text-gray-500 ml-9">
                        {isSpanish ? "Embalajes" : "Packages"}
                                  </h5>
                      {item.packageFields.map((pkg, pkgIdx) => (
                                    <div key={pkgIdx} className="ml-4 mb-2">
                                      <p className="text-xs font-medium mb-1 text-gray-500 ml-5">
                            {isSpanish ? "Embalaje" : "Package"} #{pkg.embajaIndex + 1}
                                      </p>
                          {renderFieldGroup(pkg.fields, `pkg`, FieldCard, cardProps)}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              );
});

FieldListContent.displayName = 'FieldListContent';

// Main Component
const FieldOverviewPanelComponent = ({ 
  extractedData, 
  activeSubTab, 
  setActiveSubTab, 
  isSpanish,
  validationErrors,
  taricJsonErrors,
  ValidationErrors,
  TaricJsonErrors,
  location,
  splitView,
  isFieldOverviewCollapsed = false,
  setIsFieldOverviewCollapsed = () => {},
  isSuggestedCollapsed = false,
  setIsSuggestedCollapsed = () => {},
  isFieldOverviewContentCollapsed = false,
  setIsFieldOverviewContentCollapsed = () => {},
  activeWorkflowSessionId
}) => {
  const [requiredFilter, setRequiredFilter] = useState('all');
  const prevOrganizedFieldsRef = useRef(null);
  const prevWorkflowRef = useRef(activeWorkflowSessionId);
  const [isDOMReady, setIsDOMReady] = useState(false);

  const dispatch = useDispatch();
  const declarationSummaryCollapsedByIndex = useSelector((state) => {
    const id = state.workflowSessions?.activeSessionId;
    return id ? state.workflowSessions?.sessions?.[id]?.state?.declarationSummaryCollapsedByIndex : {};
  }) || {};

  const reviewDetailsExportCollapsedByIndex = useSelector((state) => {
    const id = state.workflowSessions?.activeSessionId;
    return id ? state.workflowSessions?.sessions?.[id]?.state?.reviewDetailsExportCollapsedByIndex : {};
  }) || {};

  const onExpandExport = useCallback(
    (declIndex) => {
      if (!activeWorkflowSessionId || typeof declIndex !== 'number' || declIndex < 0) return;
      const nextSummary = { ...declarationSummaryCollapsedByIndex, [declIndex]: false };
      const nextReview = { ...reviewDetailsExportCollapsedByIndex, [declIndex]: false };
      dispatch(
        updateWorkflowSessionState(activeWorkflowSessionId, {
          declarationSummaryCollapsedByIndex: nextSummary,
          reviewDetailsExportCollapsedByIndex: nextReview,
        })
      );
    },
    [
      activeWorkflowSessionId,
      declarationSummaryCollapsedByIndex,
      reviewDetailsExportCollapsedByIndex,
      dispatch,
    ]
  );
  
  // CRITICAL: Properly detect errors - validationErrors is an array, check if it has actual errors
  const hasErrors = useMemo(() => {
    // Check validation errors - must be array with length > 0 AND pass hasValidationErrors check
    const hasValidation = validationErrors && 
      Array.isArray(validationErrors) && 
      validationErrors.length > 0 && 
      hasValidationErrors(validationErrors);
    
    // Check TARIC JSON errors
    const hasTaric = taricJsonErrors && hasTaricJsonErrors(taricJsonErrors);
    
    return hasValidation || hasTaric;
  }, [validationErrors, taricJsonErrors]);
  
  // Use ref to cache validationErrorFieldIds Set reference (Set contents comparison)
  const validationErrorFieldIdsRef = useRef(null);
  const prevValidationErrorFieldIdsHashRef = useRef(null);
  
  const validationErrorFieldIds = useMemo(() => {
    const fieldIds = getAllRequiredFieldIds(extractedData);
    // Create hash from Set contents to detect actual changes
    const hash = Array.from(fieldIds).sort().join(',');
    
    if (hash !== prevValidationErrorFieldIdsHashRef.current) {
      prevValidationErrorFieldIdsHashRef.current = hash;
      validationErrorFieldIdsRef.current = fieldIds;
    }
    
    return validationErrorFieldIdsRef.current || fieldIds;
  }, [extractedData]);
  
  const validationErrorsHash = useMemo(() => createValidationErrorsHash(validationErrors), [validationErrors]);
  
  // Only reset DOM ready when workflow changes
  useEffect(() => {
    const workflowChanged = prevWorkflowRef.current !== activeWorkflowSessionId;
    if (workflowChanged) {
      prevWorkflowRef.current = activeWorkflowSessionId;
      setIsDOMReady(false);
      clearInputElementCache();
    }
    
    if (!extractedData || (Array.isArray(extractedData) && extractedData.length === 0)) {
      setIsDOMReady(false);
      return;
    }
    
    if (!isDOMReady) {
      const timeout = setTimeout(() => setIsDOMReady(true), 500);
      return () => clearTimeout(timeout);
    }
  }, [activeWorkflowSessionId, extractedData]);

  const prevHasErrorsRef = useRef(hasErrors);

  // Auto-collapse field overview when no errors; auto-expand when errors appear.
  useEffect(() => {
    if (prevHasErrorsRef.current !== hasErrors) {
      prevHasErrorsRef.current = hasErrors;
      setIsFieldOverviewContentCollapsed(!hasErrors);
    }
  }, [hasErrors, setIsFieldOverviewContentCollapsed]);

  // Cache organizedFields and only recompute when data or errors actually change
  const organizedFields = useMemo(() => {
    if (!extractedData || (Array.isArray(extractedData) && extractedData.length === 0)) {
      return [];
    }
    
    const organized = organizeFieldsFromData(extractedData, validationErrorFieldIds);
    const result = addMissingFieldsFromErrors(organized, validationErrors, validationErrorFieldIds);
    return result;
  }, [extractedData, validationErrorFieldIds, validationErrorsHash]);
  
  const subtabs = ['allFields', 'missingFields', 'nonMissingFields'];
  const filterButtons = [
    { key: 'all', label: { en: 'All', es: 'Todos' }, className: 'bg-gray-100' },
    { key: 'required', label: { en: 'Required', es: 'Requeridos' }, className: 'bg-red-100 text-red-700' },
    { key: 'nonRequired', label: { en: 'Not Required', es: 'No Requeridos' }, className: 'bg-blue-100 text-blue-700' },
  ];
  
  return (
    <div className={`h-screen max-h-screen overflow-hidden overflow-x-hidden transition-all duration-300 ease-in-out w-72 fixed top-0 z-9w ${isFieldOverviewCollapsed ? 'right-[-100%]' : 'right-0'}`}>
      <div className="p-4 pt-6 pl-0 flex gap-4 text-left flex-col h-full max-h-full overflow-hidden ">
      {!isFieldOverviewCollapsed && (
        <>
          {/* Suggested container - 50% when both sections expanded */}
          <div className={`flex flex-col flex-shrink-0 ${isSuggestedCollapsed ? '' : 'flex-1 min-h-0'}`}>
            <button
              type="button"
              onClick={() => setIsSuggestedCollapsed(prev => !prev)}
              className={`pr-2 min-h-[40px] flex items-center justify-between gap-2 overflow-x-auto overflow-y-hidden rounded-full w-full flex-shrink-0 cursor-pointer hover:opacity-80 transition-all text-left ${isSuggestedCollapsed ? 'bg-secondary !px-4 py-3' : 'ml-4 pr-6 mb-2'}`}
            >
             <div className="flex items-center gap-2">
             <AiIconSvg className="size-5 flex-shrink-0 field-svg-icon" />
              <h2 className="text-lg font-semibold whitespace-nowrap flex-shrink-0">
                {getMainTranslation('suggested', isSpanish)}
              </h2>
             </div>
              <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${isSuggestedCollapsed ? '-rotate-90' : ''}`} />
            </button>
            {!isSuggestedCollapsed && (
              <div className="flex-1 min-h-0 overflow-hidden overflow-y-auto ml-2 flex flex-col">
                <AiSuggestionsContainer
                  activeWorkflowSessionId={activeWorkflowSessionId}
                  isSpanish={isSpanish}
                />
              </div>
            )}
          </div>

          <div className={`flex flex-col flex-shrink-0 ${!isFieldOverviewContentCollapsed ? 'flex-1 min-h-0' : ''}`}>
            <button
              type="button"
              onClick={() => setIsFieldOverviewContentCollapsed(prev => !prev)}
              className={`min-h-[40px] flex items-center justify-between gap-2 overflow-x-auto overflow-y-hidden rounded-full w-full flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity text-left ${isFieldOverviewContentCollapsed ? 'bg-secondary !px-4 py-3' : 'ml-4 pr-6 mb-2'}`}
            >
              <div className="flex items-center gap-2">
              <FieldOverviewIconSvg className="size-5 flex-shrink-0 field-svg-icon" />
                <h2 className="text-lg font-semibold whitespace-nowrap flex-shrink-0">
                  {getMainTranslation('fieldOverview', isSpanish)}
                </h2>
              </div>
              <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${isFieldOverviewContentCollapsed ? '-rotate-90' : ''}`} />
            </button>

            {!isFieldOverviewContentCollapsed && hasErrors && (
              <>
                {validationErrors && hasValidationErrors(validationErrors) && (
                  <div className="flex flex-col min-h-0 overflow-hidden flex-1">
                    <ValidationErrors validationErrors={validationErrors} hasErrors={hasErrors} isSpanish={isSpanish} splitView={splitView} onExpandExport={onExpandExport} />
                  </div>
                )}
                {taricJsonErrors && hasTaricJsonErrors(taricJsonErrors) && (
                  <div className={`flex flex-col min-h-0 overflow-hidden flex-1`}>
                    <TaricJsonErrors taricJsonErrors={taricJsonErrors} location={location} onExpandExport={onExpandExport} />
                  </div>
                )}
              </>
            )}

            {!isFieldOverviewContentCollapsed && !hasErrors && (
              <div className="flex-1 flex flex-col gap-1 min-h-0 overflow-hidden">
                <div className="flex gap-2 items-center overflow-x-auto pb-4 ml-3 flex-shrink-0">
                  {subtabs.map(tab => (
                    <button 
                      key={tab}
                      onClick={() => setActiveSubTab(tab)}
                      className={`h-8 rounded-full text-sm cursor-pointer hover:opacity-75 transition-opacity py-1 whitespace-nowrap flex-shrink-0 ${
                        activeSubTab === tab 
                          ? (tab === 'allFields' ? 'bg-gray-100 font-semibold px-3' : 'bg-gray-200 font-semibold px-3')
                          : 'pr-3'
                      }`}
                    >
                      {getMainTranslation(tab, isSpanish)}
                    </button>
                  ))}
                </div>

                <div className="flex gap-2 items-center overflow-x-auto pb-2 ml-3 flex-shrink-0">
                  {filterButtons.map(({ key, label, className }) => (
                    <button 
                      key={key}
                      onClick={() => setRequiredFilter(key)}
                      className={`h-7 rounded-full text-xs cursor-pointer hover:opacity-75 transition-opacity py-1 whitespace-nowrap flex-shrink-0 ${
                        requiredFilter === key ? `${className} font-semibold px-3` : 'pr-3 text-gray-600'
                      }`}
                    >
                      {isSpanish ? label.es : label.en}
                    </button>
                  ))}
                </div>

                <div className="flex-1 overflow-y-auto min-h-0">
                  {!extractedData || (Array.isArray(extractedData) && extractedData.length === 0) ? (
                    <p className="ml-4 text-gray-500 text-sm">{isSpanish ? 'No hay datos disponibles' : 'No data available'}</p>
                  ) : (
                    <FieldListContent
                      organizedFields={organizedFields}
                      activeSubTab={activeSubTab}
                      requiredFilter={requiredFilter}
                      validationErrorFieldIds={validationErrorFieldIds}
                      validationErrors={validationErrors}
                      isSpanish={isSpanish}
                      dataHash={`${activeWorkflowSessionId}_${extractedData ? extractedData.length : 0}`}
                      declarationSummaryCollapsedByIndex={declarationSummaryCollapsedByIndex}
                      onExpandExport={onExpandExport}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
      </div>
    </div>
  );
};

// Simple memo - only skip if nothing relevant changed
export const FieldOverviewPanel = memo(FieldOverviewPanelComponent);

FieldOverviewPanel.displayName = 'FieldOverviewPanel';
